import { useMutation } from '@tanstack/react-query'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import {
  isPlateImagingObservation,
  isPlateReadObservation,
  type PlatePage_CulturePlate,
  type PlatePage_PlateImagingObservation,
  type PlatePage_WellCulture,
} from '~/api/backend/pages/platePageAPI'
import { queryClient } from '~/api/query-client'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import Input from '~/components/Input'
import { useFeatureFlag } from '~/core/featureFlags'
import { useExtractedPlateSprite } from '~/hooks/montage-image'
import { cn } from '~/lib/utils'
import { getCellConcentrationColor } from '~/pages/Monitor/utils/cellConcentrationColors'
import { getConfluencyColor } from '~/pages/Monitor/utils/confluencyColors'
import type { WellOverlay } from '~/pages/plate/PlatePage'
import { prefersLargePlateSprite } from '~/pages/plate/plateSpriteSizing'
import { encodeURITimestamp } from '~/utils/date'
import { warmImageURL } from '~/utils/images'
import {
  MONTAGE_LAYOUT_ROWS_COLS,
  PLACEHOLDER_MONTAGE_LAYOUT_FOR_PLATE_FORMAT,
} from '~/utils/montage'
import BareCheckmarkIcon from './icons/BareCheckmarkIcon'
import CloseIcon from './icons/CloseIcon'
import CultureStatusIcon from './icons/CultureStatusIcon'
import DeleteIcon from './icons/DeleteIcon'
import ViewHiddenIcon from './icons/ViewHiddenIcon'
import cs from './plate_hero.scss'

type Culture = PlatePage_WellCulture
type PlateObservation = PlatePage_PlateImagingObservation

export default function PlateHero(props: {
  plate: PlatePage_CulturePlate
  wellOverlay: WellOverlay
  observationTimestamp?: string
  onWellToggle?: (well: string) => void
  selectedWells?: string[]
  selectionEnabled?: boolean
  selectedWell?: string
  onWellClick?: (well: string) => void
  routePrefix?: string
  highlightedWell?: string
  onWellHover?: (well: string | undefined) => void
  onSpriteLoadingChange?: (isLoading: boolean) => void
}) {
  const { onSpriteLoadingChange } = props
  const hackathon2025 = useFeatureFlag('hackathon2025')
  const plate = props.plate
  const { rows: plateRows, columns: plateColumns } = plate.plateDimensions
  const numWellsInFormat = plateRows * plateColumns // Not necessarily equal to the number of cultures or images.

  const cultureByWell: { [well: string]: Culture | undefined } = useMemo(() => {
    return Object.fromEntries(
      plate.wellCultures.map(culture => [culture.well, culture]),
    )
  }, [plate.wellCultures])

  // By default, we care about plate imaging observation
  const plateImageObservation = plate.plateObservationHistory
    .filter(isPlateImagingObservation)
    .find(obs => obs.timestamp === props.observationTimestamp)

  const plateReadObservation = plate.plateObservationHistory
    .filter(isPlateReadObservation)
    .find(obs => obs.timestamp === props.observationTimestamp)

  // Extract stable values from observation
  // Prefer v2 sprite (bounded final dimensions) over v1 (unbounded sub-image dimensions)
  // Use large sprite for high-DPI displays or large viewports to avoid upscaling blur
  const preferLargeSprite = prefersLargePlateSprite()
  const spriteImageUrlV2 = preferLargeSprite
    ? (plateImageObservation?.sprite?.imageUrlLarge ??
      plateImageObservation?.sprite?.imageUrlStandard)
    : plateImageObservation?.sprite?.imageUrlStandard
  const spriteImageUrlV1 = plateImageObservation?.sprite?.imageUrl
  const spriteMontageLayout = plateImageObservation?.sprite?.montageLayout
  const spriteWellSequence = plateImageObservation?.sprite?.wellSequence

  // Derive montage layout and dimensions
  const montageLayout =
    spriteMontageLayout ?? PLACEHOLDER_MONTAGE_LAYOUT_FOR_PLATE_FORMAT[numWellsInFormat]
  const [montageRows, montageColumns] = MONTAGE_LAYOUT_ROWS_COLS[montageLayout]
  const numSitesPerMontage = montageRows * montageColumns

  // Warm sprite URLs
  const warmedSpriteUrlV2 = spriteImageUrlV2 ? warmImageURL(spriteImageUrlV2) : null
  const warmedSpriteUrlV1 = spriteImageUrlV1 ? warmImageURL(spriteImageUrlV1) : null

  // ["A1", "A6", "B4", ...] -> {A1: 0, A6: 1, B4: 2, ...}
  const spriteWellIndices = Object.fromEntries(
    spriteWellSequence?.map((well, i) => [well, i]) ?? [],
  )

  // Try v2 sprite first (bounded dimensions, smaller file size)
  const {
    data: extractedPlateSpriteV2,
    isLoading: isSpriteLoadingV2,
    isError: isSpriteErrorV2,
  } = useExtractedPlateSprite(
    warmedSpriteUrlV2,
    numSitesPerMontage,
    spriteWellSequence,
    !hackathon2025 && !!warmedSpriteUrlV2,
  )

  // Fall back to v1 sprite if v2 fails or doesn't exist
  const shouldTryV1 = !warmedSpriteUrlV2 || (isSpriteErrorV2 && !isSpriteLoadingV2)
  const { data: extractedPlateSpriteV1, isLoading: isSpriteLoadingV1 } =
    useExtractedPlateSprite(
      warmedSpriteUrlV1,
      numSitesPerMontage,
      spriteWellSequence,
      !hackathon2025 && shouldTryV1 && !!warmedSpriteUrlV1,
    )

  // Use v2 if available, otherwise v1
  const extractedPlateSprite = extractedPlateSpriteV2 ?? extractedPlateSpriteV1
  const isSpriteLoading = isSpriteLoadingV2 || (shouldTryV1 && isSpriteLoadingV1)
  // Effective sprite URL for CSS background fallback
  const warmedSpriteUrl = shouldTryV1 ? warmedSpriteUrlV1 : warmedSpriteUrlV2

  // Notify parent when sprite loading state changes (for preloading trigger)
  useEffect(() => {
    onSpriteLoadingChange?.(isSpriteLoading)
  }, [isSpriteLoading, onSpriteLoadingChange])

  const location = useLocation()
  const currentURLSearchParams = new URLSearchParams(location.search)
  const client = useBackendClient()
  const [isLocalSelectionEnabled, setIsLocalSelectionEnabled] = useState(false)
  const [localSelectedWell, setLocalSelectedWell] = useState<string | undefined>(
    undefined,
  )
  const [absorbanceInput, setAbsorbanceInput] = useState<string>(
    plate.control_absorbance.toString(),
  )

  const selectionAllowed = ['status', 'absorbance', 'concentration'].includes(
    props.wellOverlay.key,
  )

  const isSelectingControlWell =
    (props.selectionEnabled ?? isLocalSelectionEnabled) || props.selectedWells != null

  const effectiveSelectedWell = props.selectedWell ?? localSelectedWell

  useEffect(() => {
    if (effectiveSelectedWell && plateReadObservation) {
      const v = plateReadObservation.absorbances.find(
        a => a.well === effectiveSelectedWell,
      )?.value
      setAbsorbanceInput(v != null ? v.toFixed(2) : '')
    }
  }, [effectiveSelectedWell, plateReadObservation])

  const { isLoading: isSaving, mutate: mutateControl } = useMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries([QUERY_KEYS.PLATE_PAGE, plate.id])
    },
    mutationFn: ({ plateID, absorbance }: { plateID: string; absorbance: string }) =>
      client.POST('/api/pages/plate-page/{plate_extid}/absorbance_control_value', {
        params: {
          path: { plate_extid: plateID },
          query: { control_value: absorbance },
        },
      }),
  })

  // Memoize min/max calculations to avoid recomputing on every render
  const { minAbsorbance, maxAbsorbance } = useMemo(() => {
    const absorbanceValues = plate.plateObservationHistory
      .filter(isPlateReadObservation)
      .flatMap(observation => observation.absorbances.map(abs => abs.value))
    return {
      minAbsorbance: absorbanceValues.length > 0 ? Math.min(...absorbanceValues) : 0,
      maxAbsorbance: absorbanceValues.length > 0 ? Math.max(...absorbanceValues) : 0,
    }
  }, [plate.plateObservationHistory])

  const { minCellConcentration, maxCellConcentration } = useMemo(() => {
    const cellConcentrationValues = plate.plateObservationHistory
      .filter(isPlateReadObservation)
      .flatMap(observation =>
        observation.cell_concentrations.map(concentration => concentration.value),
      )
    return {
      minCellConcentration:
        cellConcentrationValues.length > 0 ? Math.min(...cellConcentrationValues) : 0,
      maxCellConcentration:
        cellConcentrationValues.length > 0 ? Math.max(...cellConcentrationValues) : 0,
    }
  }, [plate.plateObservationHistory])

  // Get base sprite URL for stable component keys (computed once, reused for all wells)
  const baseSpriteUrl = warmedSpriteUrl?.split('?')[0]

  return (
    <div>
      <div
        className={cx(cs.container, { [cs.format96]: numWellsInFormat === 96 })}
        style={{
          ['--plate-rows' as string]: plateRows,
          ['--plate-columns' as string]: plateColumns,

          // Only load sprite via CSS if we don't have extracted images (avoids duplicate requests)
          ['--sprite-url-2x' as string]:
            warmedSpriteUrl && !extractedPlateSprite
              ? `url("${warmedSpriteUrl}")`
              : null,
        }}
      >
        {new Array(numWellsInFormat).fill(null).map((_, i) => {
          const row = Math.floor(i / plateColumns),
            col = i % plateColumns
          const rowLetter = String.fromCharCode('A'.charCodeAt(0) + row)
          const well = `${rowLetter}${col + 1}`
          const culture = cultureByWell[well]
          // Index of this well in the current sprite. The same well may have
          // different indexes in different sprites.
          const isSelected =
            effectiveSelectedWell != null
              ? effectiveSelectedWell === well
              : (props.selectedWells?.includes(well) ?? false)
          const spriteWellIndex = spriteWellIndices[well]

          const cultureLinkSearchParams =
            props.observationTimestamp && currentURLSearchParams.has('t')
              ? `?t=${encodeURITimestamp(dayjs(props.observationTimestamp))}`
              : ''

          const confluenceValue =
            plateImageObservation?.confluences?.find(c => c.well === well)?.value ?? 0

          const absorbanceValue =
            plateReadObservation?.absorbances.find(
              absorbance => absorbance.well === well,
            )?.value ?? null

          const concentrationValue =
            plateReadObservation?.cell_concentrations?.find(
              concentration => concentration.well === well,
            )?.value ?? null

          return (
            <div
              key={well}
              className={cx(cs.well, { [cs.selected]: isSelected })}
              onMouseEnter={() => props.onWellHover?.(well)}
              onMouseLeave={() => props.onWellHover?.(undefined)}
            >
              {(() => {
                // For hackathon, we default the tile to being not imaged as the background
                if (hackathon2025) {
                  return (
                    <div className={cx(cs.layer, cs.notImaged)}>
                      <NotImagedIndicator culture={culture} />
                    </div>
                  )
                }

                return spriteWellIndex != null ? (
                  <ImageryLayer
                    key={`${baseSpriteUrl}-${well}`} // Use base sprite URL (without query params) as key
                    observation={plateImageObservation}
                    spriteWellIndex={spriteWellIndex}
                    numWellsInFormat={numWellsInFormat}
                    extractedSiteImages={extractedPlateSprite?.[well]}
                  />
                ) : (
                  <div className={cx(cs.layer, cs.notImaged)}>
                    <NotImagedIndicator culture={culture} />
                  </div>
                )
              })()}

              {culture && props.wellOverlay.key === 'status' ? (
                <div
                  className={cn(
                    cs.layer,
                    cs.statusLayer,
                    hackathon2025 && absorbanceValue !== null && 'bg-heat-map relative',
                  )}
                  style={
                    hackathon2025 && absorbanceValue !== null
                      ? {
                          ['--min' as any]: minAbsorbance.toFixed(2),
                          ['--max' as any]: maxAbsorbance.toFixed(2),
                          ['--val' as any]: absorbanceValue.toFixed(2),
                        }
                      : undefined
                  }
                >
                  <div
                    className={cx(
                      cs.name,
                      cs[culture.custom_culture_status.icon.toLowerCase()],
                      hackathon2025 &&
                        absorbanceValue !== null &&
                        '!bg-transparent !text-white ',
                    )}
                  >
                    <span>{culture.custom_culture_status.name}</span>
                  </div>
                  <CultureStatusIcon
                    kind={culture.custom_culture_status.icon}
                    size={
                      numWellsInFormat < 12 ? 'lg' : numWellsInFormat < 48 ? 'md' : 'sm'
                    }
                  />

                  {hackathon2025 && absorbanceValue !== null && (
                    <div className='absolute z-10 text-white  w-full h-full flex items-center justify-center left-0 top-0 font-text-tiny'>
                      {absorbanceValue.toFixed(2)}
                    </div>
                  )}
                </div>
              ) : (
                !culture &&
                props.wellOverlay.key === 'status' && (
                  <div className={cx(cs.layer, cs.notImaged)}>
                    <NotImagedIndicator culture={undefined} />
                  </div>
                )
              )}

              {culture &&
              props.wellOverlay.key === 'confluence' &&
              confluenceValue != null ? (
                <div className={cx(cs.layer, cs.confluenceLayer)}>
                  <div className={cs.name}>
                    <span
                      style={{
                        color: getConfluencyColor(confluenceValue, true),
                      }}
                    >
                      {Math.round(confluenceValue)}%
                    </span>
                  </div>
                </div>
              ) : null}

              {culture &&
              props.wellOverlay.key === 'absorbance' &&
              absorbanceValue != null ? (
                <div
                  className='absolute w-full h-full top-0 left-0'
                  onClick={() => {
                    if (hackathon2025) {
                      setLocalSelectedWell(well)
                      setIsLocalSelectionEnabled(false)
                    } else {
                      props.onWellToggle?.(well)
                    }
                  }}
                >
                  <div
                    className='h-full w-full flex items-center justify-center bg-heat-map text-white'
                    style={{
                      ['--min' as any]: minAbsorbance.toFixed(2),
                      ['--max' as any]: maxAbsorbance.toFixed(2),
                      ['--val' as any]: absorbanceValue.toFixed(2),
                    }}
                  >
                    {absorbanceValue.toFixed(2)}
                  </div>
                </div>
              ) : null}

              {culture &&
              props.wellOverlay.key === 'concentration' &&
              concentrationValue != null ? (
                <div
                  className='absolute w-full h-full top-0 left-0 text flex items-center justify-center text-grey-dark font-text-tiny'
                  style={{
                    background: getCellConcentrationColor(concentrationValue),
                  }}
                  onClick={() => {
                    setLocalSelectedWell(well)
                    setIsLocalSelectionEnabled(false)
                  }}
                >
                  {concentrationValue.toPrecision(2)}
                </div>
              ) : null}

              {isSelectingControlWell && isSelected && (
                <div
                  className={cx(cs.layer, cs.selectedLayer)}
                  onClick={() => {
                    if (hackathon2025) {
                      setLocalSelectedWell(well)
                      setIsLocalSelectionEnabled(false)
                    } else {
                      props.onWellToggle?.(well)
                    }
                  }}
                >
                  <BareCheckmarkIcon className={cs.checkmarkIcon} />
                </div>
              )}

              {isSelectingControlWell && !isSelected && (
                <div
                  className={cx(cs.layer, cs.selectableLayer)}
                  onClick={() => {
                    if (hackathon2025) {
                      setLocalSelectedWell(well)
                      setIsLocalSelectionEnabled(false)
                    } else {
                      props.onWellToggle?.(well)
                    }
                  }}
                ></div>
              )}

              {!isSelectingControlWell && culture ? (
                <Link
                  className={cx(cs.layer, cs.linkToCulture)}
                  to={`${props.routePrefix ?? ''}/plate/${plate.id}/well/${well}${cultureLinkSearchParams}`}
                  draggable={false}
                  onClick={() => {
                    if (hackathon2025) {
                      setLocalSelectedWell(well)
                      setIsLocalSelectionEnabled(false)
                    } else {
                      props.onWellToggle?.(well)
                    }
                  }}
                />
              ) : null}
            </div>
          )
        })}
      </div>

      {(props.wellOverlay.key === 'absorbance' ||
        (hackathon2025 && props.wellOverlay.key === 'status')) && (
        <div className='flex flex-col gap-2 mt-4'>
          <div className='font-subtext'>Absorbance color legend</div>
          <div
            className={cn(
              'h-6 w-full rounded-md ',
              'flex justify-between items-center p-2 text-white',
              'heat-map-legend font-subtext',
            )}
          >
            <span>{minAbsorbance.toFixed(2)}</span>
            <span>{maxAbsorbance.toFixed(2)}</span>
          </div>
        </div>
      )}

      {props.wellOverlay.key === 'concentration' && (
        <div className='flex flex-col gap-2 mt-4'>
          <div className='font-subtext'>Cell concentration color legend</div>
          <div
            className={cn(
              'h-6 w-full rounded-md ',
              'flex justify-between items-center p-2 text-white',
              'confluency-gradient font-subtext',
            )}
          >
            <span>{minCellConcentration.toExponential(2)}</span>
            <span>{maxCellConcentration.toExponential(2)}</span>
          </div>
        </div>
      )}

      {hackathon2025 &&
        (props.wellOverlay.key === 'absorbance' ||
          props.wellOverlay.key === 'status') && (
          <div className='flex gap-4 items-center mt-4'>
            <span className='font-subtext text-grey-medium'>CONTROL VALUE</span>
            <Input
              className='flex'
              value={absorbanceInput}
              onChange={e => setAbsorbanceInput(e)}
              placeholder=''
              disabled={isSaving}
            />
            <Button
              label={isSelectingControlWell ? 'Finish selecting' : 'Select well'}
              type='primary'
              onClick={() => setIsLocalSelectionEnabled(s => !s)}
              disabled={!selectionAllowed || isSaving}
              popoverMessageIfDisabled={
                !selectionAllowed
                  ? 'Select wells is only available for Culture Status, Absorbance, or Concentration'
                  : undefined
              }
            />
            <Button
              label='Submit'
              type='normal'
              onClick={() => {
                mutateControl({ plateID: plate.id, absorbance: absorbanceInput })
                setLocalSelectedWell(undefined)
                setIsLocalSelectionEnabled(false)
              }}
              disabled={isSaving}
            />
          </div>
        )}
    </div>
  )
}

// Site image component using img tag with data URLs
function SiteImage({
  site,
  siteImageUrl,
  className,
}: {
  site: number
  siteImageUrl?: string
  className: string
}) {
  // Use img element with data URL to prevent network requests
  if (siteImageUrl) {
    return (
      <div className={cn(className, 'relative')}>
        <img
          src={siteImageUrl}
          alt=''
          className='absolute top-0 left-0 w-full h-full object-cover'
        />
      </div>
    )
  }

  // Fallback: Only set --site-index when using sprite positioning
  return <div className={className} style={{ ['--site-index' as string]: site }} />
}

function ImageryLayer({
  observation,
  spriteWellIndex,
  numWellsInFormat,
  extractedSiteImages,
}: {
  observation?: PlateObservation
  spriteWellIndex: number
  numWellsInFormat: number
  extractedSiteImages?: string[]
}) {
  const montageLayout =
    observation?.sprite?.montageLayout ??
    PLACEHOLDER_MONTAGE_LAYOUT_FOR_PLATE_FORMAT[numWellsInFormat]
  const [montageRows, montageColumns] = MONTAGE_LAYOUT_ROWS_COLS[montageLayout]
  const numSitesPerMontage = montageRows * montageColumns

  return (
    <div
      className={cx(cs.layer, cs.imagery)}
      style={{
        ['--montage-rows' as string]: montageRows,
        ['--montage-columns' as string]: montageColumns,
        ['--num-sites-per-montage' as string]: numSitesPerMontage,

        // --sprite-num-wells is the number of wells captured in the sprite.
        // This is not always the number of wells or cultures on the plate, and
        // it can vary from one imaging run to another.
        ['--sprite-num-wells' as string]: observation?.sprite?.wellSequence?.length,
        ['--sprite-well-index' as string]: spriteWellIndex,
      }}
    >
      {new Array(numSitesPerMontage).fill(null).map((_, site) => (
        <SiteImage
          key={site}
          site={site}
          siteImageUrl={extractedSiteImages?.[site]}
          className={cs.site}
        />
      ))}
    </div>
  )
}

function NotImagedIndicator({ culture }: { culture?: Culture }) {
  const isTerminated =
    culture?.custom_culture_status?.icon === 'TERMINATED' ||
    culture?.status === 'Terminated'

  if (isTerminated) {
    return (
      <>
        <DeleteIcon className={cs.icon} />
        <div className={cs.iconTooltip}>Terminated</div>
      </>
    )
  }

  if (culture != null) {
    return (
      <>
        <ViewHiddenIcon className={cs.icon} />
        <div className={cs.iconTooltip}>Not Imaged</div>
      </>
    )
  }

  return (
    <>
      <CloseIcon className={cs.icon} />
      <div className={cs.iconTooltip}>Empty</div>
    </>
  )
}

/**
 * A very basic loading skeleton for situations where most plate data isn't in
 * the cache yet.
 *
 * For example, if the user started their session on the well page, we may not
 * have all the plate data, but we know the dimensions, so we can render a large
 * portion of the visual layout much faster using this placeholder.
 *
 * TODO(SWE-1840): We don't cache responses yet in backend v2, but once we do,
 * we should render this component when possible.
 */
export function PlateHeroLoadingPlaceholder({
  cached,
}: {
  cached: PlatePage_CulturePlate
}) {
  const { rows: plateRows, columns: plateColumns } = cached.plateDimensions
  const numWellsInFormat = plateRows * plateColumns // Not necessarily equal to the number of cultures or images.

  return (
    <div
      className={cx(cs.container, cs.loadingPlaceholder, {
        [cs.format96]: numWellsInFormat === 96,
      })}
      style={{
        ['--plate-rows' as string]: plateRows,
        ['--plate-columns' as string]: plateColumns,
      }}
    >
      {new Array(numWellsInFormat).fill(null).map((_, i) => {
        return (
          <div key={i} className={cs.well}>
            <div className={cx(cs.layer, cs.notImaged)}></div>
          </div>
        )
      })}
    </div>
  )
}
